// Home layout selection (pure, so it is unit-testable without rendering the page).
//
// A plain chat is a conversation, never a project: the conversation view only shows the thread and its pinned
// composer. A resumed conversation (route `/chats/:sessionId`) is a conversation from the first paint, so its composer
// is pinned to the footer immediately instead of waiting for the history request to flip `askStarted`.

export type HomeMode = "build" | "ask";
export type HomeView = "initial" | "conversation";

export function homeViewOf(mode: HomeMode, askStarted: boolean, sessionId: string | undefined): HomeView {
  return mode === "ask" && (askStarted || sessionId !== undefined) ? "conversation" : "initial";
}

/**
 * Whether the Home submit path (project creation + PRD import) may run. Only the Build mode creates projects; a plain
 * chat message goes through the assistant turn routes and never creates a project row.
 */
export function mayCreateProjectFromHome(mode: HomeMode): boolean {
  return mode === "build";
}

// Only real greetings: a one-word brief (a product or project name) must not be mistaken for small talk.
const GREETING_WORDS = new Set([
  "oi", "ola", "hello", "hi", "hey", "eai", "bom", "boa", "dia", "tarde", "noite", "tudo", "bem",
  "opa", "salve", "obrigado", "obrigada", "thanks",
]);

/**
 * A chat-style message (only greetings, or nothing) is not a project brief: sending it from the Build composer
 * would create and open a project named after it. Any brief that is not all greetings and not empty may
 * create a project. A PRD file or attachments always make a real brief (`hasFiles`).
 */
export function isTrivialProjectBrief(text: string, hasFiles: boolean): boolean {
  if (hasFiles) return false;
  const words = text
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/\be\s+ai\b/g, "eai") // The two-word greeting "e ai" (without its accent) is joined first, else its words would read as a brief
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => w.length > 0);
  return words.every((w) => GREETING_WORDS.has(w));
}
